import { Room, RoomVisibility } from "@/types/rooms";
import { RoomCard } from "./RoomCard";
import { Plus, Box } from "lucide-react";
import { TeamIcon } from "@/components/icons/TeamIcon";
import { useTranslation } from "@/context/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";

interface Props {
  rooms: Room[];
  onCreateRoom: () => Promise<void> | void;
  onCreateTeamRoom?: () => Promise<void> | void;
  onRenameRoom: (id: string, name: string) => Promise<void> | void;
  onDeleteRoom: (id: string) => Promise<void> | void;
  onChangeVisibility: (id: string, visibility: RoomVisibility) => Promise<void> | void;
}

export function RoomsGrid({
  rooms,
  onCreateRoom,
  onCreateTeamRoom,
  onRenameRoom,
  onDeleteRoom,
  onChangeVisibility,
}: Props) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = () => {
    if (onCreateTeamRoom) {
      setDialogOpen(true);
    } else {
      onCreateRoom();
    }
  };

  return (
    <div className="w-full mb-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
        <button
          className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border rounded-2xl h-[158px] bg-card hover:border-border/60 transition-all cursor-pointer"
          onClick={handleClick}
        >
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center mb-3">
            <Plus className="h-6 w-6 text-muted-foreground" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">{t("dashboard.createRoom")}</span>
        </button>

        {rooms.map((room) => (
          <RoomCard
            key={room.id}
            room={room}
            onRename={onRenameRoom}
            onDelete={onDeleteRoom}
            onChangeVisibility={onChangeVisibility}
          />
        ))}
      </div>

      {onCreateTeamRoom && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-center">{t("dashboard.createRoom")}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => { setDialogOpen(false); onCreateRoom(); }}
                className="flex flex-col items-center gap-3 rounded-xl border-2 border-border bg-card p-5 hover:border-primary/30 hover:bg-primary/5 transition-all"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Box className="h-5 w-5 text-muted-foreground" />
                </div>
                <span className="text-sm font-medium text-foreground">Personal Room</span>
              </button>
              <button
                onClick={() => { setDialogOpen(false); onCreateTeamRoom(); }}
                className="flex flex-col items-center gap-3 rounded-xl border-2 border-border bg-card p-5 hover:border-[hsl(var(--selected)/0.4)] hover:bg-[hsl(var(--selected-subtle))] transition-all"
              >
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--selected-subtle))] flex items-center justify-center">
                  <TeamIcon className="h-5 w-5 text-[hsl(var(--selected))]" size={20} />
                </div>
                <span className="text-sm font-medium text-foreground">Team Room</span>
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
